import { Dialog, Tabs } from 'radix-ui';
import './styles.scss';
import { GameButton } from '../../gui/GameButton';
import { Slider, Checkbox } from '@game-libs/game-menu-creator/src/components';
import { useEffect, useState } from 'react';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { EventsManager } from '@game-libs/events-manager';
import { AudioManager } from '@game-libs/audio-manager';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { GUIActions } from '../../types/game-types';
import { useHotkeys } from 'react-hotkeys-hook';
import { MenuEvents } from '@game-libs/models';
import { Icon } from '../../gui/Icon';
import { CreditsTab } from './CreditsTab';

export const SettingsMenu = () => {
  const [confirmClearSave, setConfirmClearSave] = useState(false);
  const [selectedTab, setSelectedTab] = useState('game');
  const { playSFXNow } = AudioManager();
  const { actions, settings: storeSettings, state: globalState } = GlobalGameStore();
  const [isOpen, setIsOpen] = useState(false);
  const { emit } = EventsManager();
  const { playSFX } = AudioManager();
  const { t } = useTranslation('menus');

  // Konami Code
  const [versionClickCount, setVersionClickCount] = useState(0);
  const handleVersionClick = () => {
    setVersionClickCount(() => versionClickCount + 1);
    if (versionClickCount === 6) {
      actions.setGlobalVariables({
        // unlockedEndlessMode: true,
      });
    }
  };

  const {
    musicVolume: initialMusicVolume,
    sfxVolume: initialSfxVolume,
    crtFilterEnabled: initialCrtFilterEnabled,
    chromaticAberration: initialChromaticAberration = 10,
    isFullscreen,
    useAlternativeFont: initialUseAlternativeFont,
    largerTextSize: initialLargerTextSize,
    language: storeLanguage = 'en',
  } = storeSettings.value;

  const { shootsCats, catSounds, swordSwooshSounds } = globalState.globalVariables.value;

  useHotkeys(
    'ESC',
    () => {
      if (isOpen) {
        setIsOpen(false);
        playSFXNow(MenuEvents.CHANGE_VALUE);
      } else {
        setIsOpen(true);
        playSFX('chains');
      }
    },
    [isOpen],
    { enableOnFormTags: true },
  );

  const handleClearSave = () => {
    emit(GUIActions.CLEAR_SAVE);
  };

  const handleQuitGame = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (import.meta.env.VITE_SAVE_BACKEND === 'gemshell') {
      await gemshell?.app.quit();
    } else {
      nw?.App.closeAllWindows();
    }
  };

  const handleFullscreen = (value: boolean) => {
    actions.setSettings({
      isFullscreen: value,
    });

    if (import.meta.env.VITE_SAVE_BACKEND === 'gemshell') {
      gemshell?.window.setFullscreen(!isFullscreen);
    } else if (value) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleLanguageChange = (lang: string) => {
    playSFXNow(MenuEvents.CHANGE_VALUE);
    actions.setSettings({ language: lang });
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    i18n.changeLanguage(storeLanguage);
  }, [storeLanguage]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--font-primary',
      initialUseAlternativeFont ? 'everyday_slight' : 'pixelAe',
    );
    (document.documentElement as HTMLElement).style.setProperty(
      '--font-multiplier',
      initialUseAlternativeFont ? '1' : '1.1',
    );
    (document.documentElement as HTMLElement).style.setProperty(
      '--larger-text-size',
      initialLargerTextSize ? '1' : '0',
    );

    if (import.meta.env.VITE_SAVE_BACKEND === 'gemshell') {
      gemshell?.window.setFullscreen(isFullscreen);
    } else {
      if (isFullscreen) {
        document.documentElement.requestFullscreen();
      } else {
        document.exitFullscreen().catch(() => {
          console.warn('Error exiting fullscreen. Maybe already in windowed mode?');
        });
      }
    }

    if (isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen().catch(() => {
        console.warn('Error exiting fullscreen. Maybe already in windowed mode?');
      });
    }
  }, [initialUseAlternativeFont, initialLargerTextSize, isFullscreen]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <Dialog.Trigger asChild>
        <GameButton
          id="settings"
          labelId="settings"
          onClick={() => {
            playSFX('chains');
          }}
          hotkey={'ESC'}
        />
      </Dialog.Trigger>
      <Dialog.Portal container={document.getElementById('ui-wrapper')}>
        <Dialog.Overlay className="DialogOverlay" />
        <Dialog.Content className="menu-container" onEscapeKeyDown={(e) => e.preventDefault()}>
          <Dialog.Title className="menu-title">{t('settings')}</Dialog.Title>

          <Tabs.Root
            defaultValue={'game'}
            orientation="vertical"
            value={selectedTab}
            onValueChange={(value) => {
              setSelectedTab(value);
              playSFXNow(MenuEvents.CHANGE_VALUE);
            }}
          >
            <Tabs.List className="settings-tabs-list">
              <div className="inner-settings">
                <div className="settings-tabs">
                  <Tabs.Trigger key={'game'} value={'game'} data-testid="game-tab" asChild>
                    <button className={'button small'}>
                      {selectedTab === 'game' && <Icon iconId="right_arrow" />} <div> </div> {t('tabs.game')}
                    </button>
                  </Tabs.Trigger>
                  <Tabs.Trigger key={'graphics'} value={'graphics'} data-testid="graphics-tab" asChild>
                    <button className={'button small'}>
                      {selectedTab === 'graphics' && <Icon iconId="right_arrow" />} <div> </div> {t('tabs.graphics')}
                    </button>
                  </Tabs.Trigger>
                  <Tabs.Trigger key={'audio'} value={'audio'} data-testid="audio-tab" asChild>
                    <button className={'button small'}>
                      {selectedTab === 'audio' && <Icon iconId="right_arrow" />} <div> </div> {t('tabs.audio')}
                    </button>
                  </Tabs.Trigger>
                  <Tabs.Trigger key={'credits'} value={'credits'} data-testid="credits-tab" asChild>
                    <button className={'button small'}>
                      {selectedTab === 'credits' && <Icon iconId="right_arrow" />} <div> </div> {t('tabs.credits')}
                    </button>
                  </Tabs.Trigger>
                </div>

                <Tabs.Content
                  key={'audio'}
                  value={'audio'}
                  className={`settings-tab-content ${selectedTab === 'audio' ? ' active' : ''}`}
                >
                  <div className="audio-controls">
                    <div className="audio-header">{t('audioSettings')}</div>
                    <Slider
                      label={t('music')}
                      initialValue={initialMusicVolume}
                      isFirst
                      onValueChange={(value) =>
                        actions.setSettings({
                          musicVolume: +value,
                        })
                      }
                    />
                    <Slider
                      label={t('sfx')}
                      initialValue={initialSfxVolume}
                      onValueChange={(value) =>
                        actions.setSettings({
                          sfxVolume: +value,
                        })
                      }
                    />
                    <Checkbox
                      label={t('swordSwooshSounds')}
                      initialValue={swordSwooshSounds ?? true}
                      onValueChange={(value) =>
                        actions.setGlobalVariables({
                          swordSwooshSounds: value,
                        })
                      }
                    />
                    {shootsCats && (
                      <Checkbox
                        label={t('catSounds')}
                        initialValue={catSounds}
                        onValueChange={(value) =>
                          actions.setGlobalVariables({
                            catSounds: value,
                          })
                        }
                      />
                    )}
                  </div>
                </Tabs.Content>

                <Tabs.Content
                  key={'game'}
                  value={'game'}
                  className={`settings-tab-content${selectedTab === 'game' ? ' active' : ''}`}
                >
                  <div className="game-control">
                    <div className="game-header">{t('gameSettings')}</div>
                    <div className="language-container">
                      <div className="language-header">{t('language')}</div>
                      <div className="language-flags">
                        <button
                          type="button"
                          className={`language-flag ${storeLanguage === 'en' ? 'active' : ''}`}
                          onClick={() => handleLanguageChange('en')}
                          title="English"
                          aria-label="English"
                          data-testid="language-flag-en"
                        >
                          <img src="./flags/us.png" alt="English" />
                        </button>
                        <button
                          type="button"
                          className={`language-flag ${storeLanguage === 'fr' ? 'active' : ''}`}
                          onClick={() => handleLanguageChange('fr')}
                          title="French"
                          aria-label="French"
                          data-testid="language-flag-fr"
                        >
                          <img src="./flags/fr.png" alt="French" />
                        </button>
                        <button
                          type="button"
                          className={`language-flag ${storeLanguage === 'de' ? 'active' : ''}`}
                          onClick={() => handleLanguageChange('de')}
                          title="German"
                          aria-label="German"
                          data-testid="language-flag-de"
                        >
                          <img src="./flags/de.png" alt="German" />
                        </button>
                        <button
                          type="button"
                          className={`language-flag ${storeLanguage === 'pt' ? 'active' : ''}`}
                          onClick={() => handleLanguageChange('pt')}
                          title="Portuguese"
                          aria-label="Portuguese"
                          data-testid="language-flag-pt"
                        >
                          <img src="./flags/br.png" alt="Portuguese" />
                        </button>
                      </div>
                    </div>
                    <Slider
                      label={t('shakeIntensity')}
                      initialValue={storeSettings.value.shakeIntensity}
                      onValueChange={(value) =>
                        actions.setSettings({
                          shakeIntensity: +value,
                        })
                      }
                    />

                    <div className="clear-container">
                      {!confirmClearSave ? (
                        <>
                          <div className="label">{t('clear_save_label')}</div>
                          <button
                            className="button small"
                            data-testid="clear-save-button"
                            onClick={() => {
                              setConfirmClearSave(true);
                            }}
                          >
                            {t('clear_save')}
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="label">{t('are_you_sure')}</div>
                          <button
                            className="button small"
                            data-testid="clear-save-confirm-button"
                            onClick={() => {
                              setConfirmClearSave(false);
                              handleClearSave();
                            }}
                          >
                            {t('yes')}
                          </button>
                          <button
                            className="button small"
                            onClick={() => {
                              setConfirmClearSave(false);
                            }}
                          >
                            {t('no')}
                          </button>
                          <div className="clear-warning">{t('cannot_be_reversed')}</div>
                        </>
                      )}
                    </div>
                  </div>
                </Tabs.Content>

                <Tabs.Content
                  key={'credits'}
                  value={'credits'}
                  className={`settings-tab-content${selectedTab === 'credits' ? ' active' : ''}`}
                >
                  <CreditsTab />
                </Tabs.Content>

                <Tabs.Content
                  key={'graphics'}
                  value={'graphics'}
                  className={`settings-tab-content${selectedTab === 'graphics' ? ' active' : ''}`}
                >
                  <div className="graphics-controls">
                    <div className="graphics-header">{t('graphicsSettings')}</div>

                    <Checkbox
                      label={t('fullscreen')}
                      initialValue={isFullscreen ?? false}
                      onValueChange={(value) => handleFullscreen(value)}
                    />

                    {/* <div className="ui-zoom-adjustment">
                      <div className="label">{t('zoom_adjustment')}</div>
                      <button
                        className="button small"
                        onClick={() => {
                          setConfirmClearSave(true);
                        }}
                      >
                        x0.5
                      </button>
                      <button
                        className="button small"
                        onClick={() => {
                          setConfirmClearSave(true);
                        }}
                      >
                        x1
                      </button>
                      <button
                        className="button small"
                        onClick={() => {
                          setConfirmClearSave(true);
                        }}
                      >
                        x2
                      </button>
                    </div> */}

                    <Checkbox
                      label={t('largerTextSize')}
                      initialValue={initialLargerTextSize ?? false}
                      onValueChange={(value) => {
                        (document.documentElement as HTMLElement).style.setProperty(
                          '--larger-text-size',
                          value ? '1' : '0',
                        );
                        actions.setSettings({
                          largerTextSize: value,
                        });
                      }}
                    />
                    <Checkbox
                      label={t('pixelFont')}
                      initialValue={initialUseAlternativeFont}
                      dataTestId="alternative-font-checkbox"
                      onValueChange={(value) => {
                        document.body.style.setProperty('--font-primary', value ? 'everyday_slight' : 'pixelAe');
                        (document.documentElement as HTMLElement).style.setProperty(
                          '--font-multiplier',
                          value ? '1' : '1.2',
                        );
                        actions.setSettings({
                          useAlternativeFont: value,
                        });
                      }}
                    />
                    <Checkbox
                      label={t('crtFilter')}
                      initialValue={initialCrtFilterEnabled ?? false}
                      onValueChange={(value) =>
                        actions.setSettings({
                          crtFilterEnabled: value,
                        })
                      }
                    />
                    <Slider
                      enabled={initialCrtFilterEnabled}
                      label={t('chromaticAberrationSlider')}
                      initialValue={initialChromaticAberration}
                      onValueChange={(value) =>
                        actions.setSettings({
                          chromaticAberration: +value,
                        })
                      }
                    />
                  </div>
                </Tabs.Content>
                <div className={`buttons-container`}>
                  <Dialog.Close asChild onPointerDown={() => playSFXNow(MenuEvents.CHANGE_VALUE)}>
                    <button
                      className={'button'}
                      data-testid="settings-close-button"
                      onClick={() => playSFXNow(MenuEvents.CHANGE_VALUE)}
                    >
                      {t('close')}
                    </button>
                  </Dialog.Close>
                  {import.meta.env.VITE_GAME_ISDESKTOP === 'true' && (
                    <button className={'button quit-game-button'} onClick={(e) => handleQuitGame(e)}>
                      {t('quitGame')}
                    </button>
                  )}
                </div>
                <div className="version" onClick={handleVersionClick}>
                  v.{import.meta.env.VITE_GAME_VERSION}
                  {globalState.globalVariables.value.unlockedEndlessMode ? '.' : ''}
                </div>
              </div>
            </Tabs.List>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
